/**
 * Company hard-require: schedule only ICAOs from the existing Content.xml /
 * Community scan (same list Destinations / map already uses).
 *
 * Destinations catalog and OurAirports stay for coords / runway lengths.
 * This module only answers scheduling eligibility.
 */
import { hydrateSceneryAirport } from "./airports";
import { sceneryStubFromIcao } from "./sceneryScan";
import type { CrewMember, SceneryAirport, SceneryPresence } from "./types";

export const CONTENT_XML_SCHEDULE_EMPTY_MSG =
  "Schedule only Content.xml airports is on, but the Community / Content.xml list is empty. Rescan or import Content.xml in Settings.";

export const CONTENT_XML_SCHEDULE_TOO_FEW_MSG =
  "Schedule only Content.xml airports is on, but fewer than two scanned ICAOs can be used as a city-pair. Rescan or import Content.xml in Settings.";

export function contentXmlScheduleIcaos(
  inventory: Array<string | { icao?: string | null } | null | undefined> | null | undefined,
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const row of inventory ?? []) {
    const raw = typeof row === "string" ? row : row?.icao;
    const icao = (raw ?? "").trim().toUpperCase();
    if (!icao || icao === "TBD" || seen.has(icao)) continue;
    seen.add(icao);
    out.push(icao);
  }
  return out.sort();
}

/** One crew member’s Community / Content.xml scan — never another desk’s list. */
export function contentXmlInventoryForCrew(
  crew: readonly CrewMember[] | null | undefined,
  crewId?: string | null,
): SceneryPresence[] {
  const id = (crewId ?? "").trim();
  if (!id) return [];
  const member = (crew ?? []).find((row) => row.id === id);
  if (!member) return [];
  return contentXmlInventoryFromList(member.sceneryInventory);
}

function contentXmlInventoryFromList(
  inventory: readonly SceneryPresence[] | null | undefined,
): SceneryPresence[] {
  const byIcao = new Map<string, SceneryPresence>();
  for (const row of inventory ?? []) {
    const code = (row.icao ?? "").trim().toUpperCase();
    if (!code || byIcao.has(code)) continue;
    byIcao.set(code, { ...row, icao: code });
  }
  return [...byIcao.values()].sort((a, b) => a.icao.localeCompare(b.icao));
}

/**
 * Schedule against the assignee’s scan. No assignee → admin / dispatcher on
 * this desk. Never union every roster row (that mixed friend + Emerson scans).
 */
export function contentXmlInventoryForAssignee(
  crew: readonly CrewMember[] | null | undefined,
  assigneeCrewId?: string | null,
  fallbackCrewId?: string | null,
): SceneryPresence[] {
  const assigned = contentXmlInventoryForCrew(crew, assigneeCrewId);
  if (assigneeCrewId?.trim()) return assigned;
  const fallback = contentXmlInventoryForCrew(crew, fallbackCrewId);
  if (fallbackCrewId?.trim()) return fallback;
  const admin = (crew ?? []).find((row) => row.isAdmin) ?? (crew ?? []).find((row) => row.roles.includes("dispatcher"));
  return admin ? contentXmlInventoryFromList(admin.sceneryInventory) : [];
}

/** @deprecated Prefer contentXmlInventoryForAssignee — union mixed per-desk scans. */
export function contentXmlInventoryFromCrew(
  crew: readonly CrewMember[] | null | undefined,
): SceneryPresence[] {
  const byIcao = new Map<string, SceneryPresence>();
  for (const member of crew ?? []) {
    for (const row of member.sceneryInventory ?? []) {
      const code = (row.icao ?? "").trim().toUpperCase();
      if (!code || byIcao.has(code)) continue;
      byIcao.set(code, { ...row, icao: code });
    }
  }
  return [...byIcao.values()].sort((a, b) => a.icao.localeCompare(b.icao));
}

export function missingSceneryForCrew(
  member: Pick<CrewMember, "sceneryInventory"> | null | undefined,
  icaos: Array<string | null | undefined>,
): string[] {
  const allowed = new Set(contentXmlScheduleIcaos(member?.sceneryInventory));
  return contentXmlScheduleIcaos(icaos).filter((icao) => !allowed.has(icao));
}

export function inventoryIcaosFromPrefers(input: {
  preferIcaos?: string[] | null;
  techStopInventory?: string[] | null;
}): string[] {
  return contentXmlScheduleIcaos([...(input.preferIcaos ?? []), ...(input.techStopInventory ?? [])]);
}

export function contentXmlScheduleMissingMessage(icaos: string[]): string {
  const who = icaos.join(", ");
  const verb = icaos.length === 1 ? "is" : "are";
  return `${who} ${verb} not in the scanned Content.xml / Community list. Rescan Content.xml or turn off “Schedule only Content.xml airports”.`;
}

export function contentXmlScheduleBlocked(
  enabled: boolean,
  inventory: Array<string | { icao?: string | null } | null | undefined> | null | undefined,
  icaos: Array<string | null | undefined>,
): string | undefined {
  if (!enabled) return undefined;
  const allowed = new Set(contentXmlScheduleIcaos(inventory));
  if (!allowed.size) return CONTENT_XML_SCHEDULE_EMPTY_MSG;
  const missing = contentXmlScheduleIcaos(icaos).filter((icao) => !allowed.has(icao));
  if (!missing.length) return undefined;
  return contentXmlScheduleMissingMessage(missing);
}

export function restrictSceneryToContentXml(
  scenery: readonly SceneryAirport[],
  inventory: readonly string[],
): SceneryAirport[] {
  const allow = new Set(inventory.map((c) => c.trim().toUpperCase()).filter((c) => c && c !== "TBD"));
  const fromScenery: SceneryAirport[] = [];
  const have = new Set<string>();
  for (const row of scenery) {
    const code = row.icao.trim().toUpperCase();
    if (!allow.has(code) || have.has(code)) continue;
    have.add(code);
    fromScenery.push(row);
  }
  const extras: SceneryAirport[] = [];
  for (const icao of allow) {
    if (have.has(icao)) continue;
    extras.push(hydrateSceneryAirport(icao, [...scenery]) ?? sceneryStubFromIcao(icao));
  }
  return [...fromScenery, ...extras];
}

export function applyContentXmlScheduleToScenery(
  scenery: readonly SceneryAirport[],
  enabled: boolean,
  inventory: readonly string[],
): { ok: true; scenery: SceneryAirport[] } | { ok: false; error: string } {
  if (!enabled) return { ok: true, scenery: [...scenery] };
  if (!inventory.length) return { ok: false, error: CONTENT_XML_SCHEDULE_EMPTY_MSG };
  const next = restrictSceneryToContentXml(scenery, inventory);
  if (next.length < 2) return { ok: false, error: CONTENT_XML_SCHEDULE_TOO_FEW_MSG };
  return { ok: true, scenery: next };
}

export function icaosFromRoute(row: {
  originIcao?: string | null;
  destIcao?: string | null;
  techStopIcao?: string | null;
  viaIcao?: string | null;
  proposedLegs?: Array<{ originIcao?: string | null; destIcao?: string | null }> | null;
  legs?: Array<{ originIcao?: string | null; destIcao?: string | null }> | null;
}): string[] {
  const raw = [
    row.originIcao,
    row.destIcao,
    row.techStopIcao,
    row.viaIcao,
    ...(row.proposedLegs ?? []).flatMap((leg) => [leg.originIcao, leg.destIcao]),
    ...(row.legs ?? []).flatMap((leg) => [leg.originIcao, leg.destIcao]),
  ];
  return contentXmlScheduleIcaos(raw);
}
